import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * BackToTopButton — fixed-position circular button that smoothly
 * scrolls the page to the top.
 *
 * Hidden by default; appears once the user has scrolled past roughly
 * one viewport height so it doesn't clutter the screen above the fold.
 * Styled in global.css under the `.back-to-top` class.
 *
 * Mounted once in App.jsx, so it shows on every route.
 */
function BackToTopButton() {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const threshold = Math.max(300, window.innerHeight * 0.6)

    const onScroll = () => {
      setVisible(window.scrollY > threshold)
    }

    // Set initial state in case the page loads already scrolled
    // (e.g. browser back-button restoration).
    onScroll()

    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <button
      type="button"
      className={`back-to-top ${visible ? 'is-visible' : ''}`}
      onClick={handleClick}
      aria-label={t('nav.backToTop')}
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
    >
      <svg
        viewBox="0 0 24 24"
        width="22"
        height="22"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M12 5l-7 7h4v7h6v-7h4z"
          fill="currentColor"
        />
      </svg>
    </button>
  )
}

export default BackToTopButton
